import React, { useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import LoadingInterstitial from "@components/LoadingInterstitial";

import { useAuthenticatedUser, useAuthentication } from "@lib/authentication";
import LoginForm, { LoginFormModes } from "@pages/Auth/LoginForm";
import PasswordForm, { PasswordFormMode } from "@pages/Auth/PasswordForm";

import { FormContainer } from "./components/FormContainer";

const AuthPage = ({ children }: { children?: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: isLoadingAuthenticatedUser } =
    useAuthenticatedUser();
  const { signOut } = useAuthentication();

  useEffect(() => {
    if (location.pathname === "/logout") {
      signOut().then(() => {
        localStorage.removeItem("si");
        localStorage.removeItem("ck");
        if (import.meta.env.VITE_ENV !== "local") {
          window.location.href = "/login";
        } else {
          navigate("/");
        }
      });
    }
  }, [location, navigate, signOut]);

  // Login / password-reset success. Navigation into the admin is handled by the
  // isAuthenticated <Navigate> below; the legacy Java session bridge
  // (GET /collections-api/authenticate) was removed here (OMG-2089), so there is
  // nothing else to do on success.
  const onSuccess = () => {};

  if (location.pathname === "/logout" || isLoadingAuthenticatedUser)
    return <LoadingInterstitial />;

  if (
    isAuthenticated &&
    !["/logout", "/change-password"].includes(location.pathname)
  ) {
    return (
      <Navigate to={`/home${location.search}`} state={{ fromLogin: true }} />
    );
  }
  const authMode = location.pathname.slice(1);

  if (LoginFormModes.includes(authMode)) {
    const loginFormMode = authMode as "login" | "forgot-password";
    if (localStorage.getItem("si")) {
      return (
        <>
          <LoadingInterstitial />
          <LoginForm onSuccess={onSuccess} mode={loginFormMode} />
        </>
      );
    }
    return (
      <FormContainer>
        <LoginForm onSuccess={onSuccess} mode={loginFormMode} />
      </FormContainer>
    );
  }

  if (PasswordFormMode.includes(authMode)) {
    const passwordFormMode = authMode as "reset-password" | "change-password";
    const searchParams = new URLSearchParams(location.search);
    return (
      <FormContainer>
        <PasswordForm
          mode={passwordFormMode}
          email={searchParams.get("email")}
          code={searchParams.get("code")}
          callback={onSuccess}
        />
      </FormContainer>
    );
  }

  return (
    <div className="min-h-screen flex p-16 sm:px-30 bg-auth-bg bg-auth-gradient">
      {children}
    </div>
  );
};

export default AuthPage;
